import React, { useState } from "react";
import { auth, db } from "../lib/firebase";
import { login } from "../lib/auth";
import { createUserWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, reload } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";

export default function LoginPage() {
  const [mode,     setMode]     = useState("login");
  const [name,     setName]     = useState("");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [phone,    setPhone]    = useState("");
  const [address,  setAddress]  = useState("");
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");
  const [loading,  setLoading]  = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);

  const switchMode = (m) => {
    setMode(m); setError(""); setSuccess("");
    setName(""); setEmail(""); setPassword(""); setConfirm("");
    setVerifying(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const profile = await login(email, password);
      if (auth.currentUser && !auth.currentUser.emailVerified && profile?.role !== "admin") {
        await auth.signOut();
        setError("Please verify your email before signing in. Check your inbox for the verification link.");
        return;
      }
    } catch (err) {
      setError(err.code === "auth/too-many-requests"
        ? "Too many attempts. Please wait a moment."
        : "Incorrect email or password.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim())        { setError("Please enter your full name."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, "users", cred.user.uid), {
        email:     email.toLowerCase().trim(),
        name:      name.trim(),
        phone:     phone.trim(),
        address:   address.trim(),
        role:      "customer",
        createdAt: serverTimestamp(),
      });
      await sendEmailVerification(cred.user, { url: window.location.origin });
      await auth.signOut();
      setVerifying(true);
    } catch (err) {
      if (err.code === "auth/email-already-in-use") setError("This email is already registered. Please sign in.");
      else if (err.code === "auth/invalid-email")   setError("Please enter a valid email address.");
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError("Please enter your email address."); return; }
    setError(""); setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email.toLowerCase().trim());
      setSuccess("Password reset email sent! Check your inbox and follow the link.");
    } catch (err) {
      if (err.code === "auth/user-not-found") setError("No account found with this email.");
      else if (err.code === "auth/invalid-email") setError("Please enter a valid email address.");
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVerified = async () => {
    setCheckingVerification(true); setError("");
    try {
      await login(email, password);
      if (auth.currentUser && !auth.currentUser.emailVerified) {
        await reload(auth.currentUser);
        if (!auth.currentUser.emailVerified) {
          await auth.signOut();
          setError("Email not verified yet. Please check your inbox and click the link.");
        }
      }
    } catch (e) {
      setError("Could not check. Please try signing in manually.");
    } finally {
      setCheckingVerification(false);
    }
  };

  // ── Verification waiting screen ──
  if (verifying) {
    return (
      <div className="login-wrap">
        <div className="login-box">
          <div className="login-header">
            <div className="login-icon-wrap" style={{ background: "#378ADD" }}>
              <svg width="30" height="30" fill="none" viewBox="0 0 24 24">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#fff" strokeWidth="2"/>
                <polyline points="22,6 12,13 2,6" stroke="#fff" strokeWidth="2"/>
              </svg>
            </div>
            <h1 className="login-title">Check your email</h1>
            <p className="login-subtitle">We sent a verification link to</p>
            <p style={{ fontWeight: 700, marginTop: 4, fontSize: 15 }}>{email}</p>
          </div>
          <div className="card" style={{ textAlign: "center" }}>
            {error && <div className="error-msg">{error}</div>}
            <p style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 20, lineHeight: 1.7 }}>
              Click the link in your email to verify your account, then come back here and sign in.
            </p>
            <button className="btn btn-primary btn-full" onClick={handleCheckVerified} disabled={checkingVerification} style={{ marginBottom: 10 }}>
              {checkingVerification ? "Checking..." : "I verified my email — Sign in"}
            </button>
            <button className="btn btn-ghost btn-full" onClick={() => switchMode("login")}>
              Back to sign in
            </button>
            <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 14 }}>
              Didn't receive it? Check your spam folder.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <div className="login-box">
        <div className="login-header">
          <div className="login-icon-wrap">
            <svg width="30" height="30" fill="none" viewBox="0 0 24 24">
              <path d="M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2Z" stroke="#fff" strokeWidth="2"/>
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="#fff" strokeWidth="2"/>
              <line x1="12" y1="12" x2="12" y2="17" stroke="#fff" strokeWidth="2"/>
              <line x1="9" y1="14.5" x2="15" y2="14.5" stroke="#fff" strokeWidth="2"/>
            </svg>
          </div>
          <h1 className="login-title">LebMarket Shipping</h1>
          <p className="login-subtitle">
            {mode === "login"          ? "Sign in to track your packages" :
             mode === "signup"         ? "Create your account" :
             "Reset your password"}
          </p>
        </div>

        {/* Mode toggle — hide on forgot password */}
        {mode !== "forgot" && (
          <div style={{ display: "flex", background: "var(--bg-soft)", borderRadius: "var(--radius)", padding: 4, marginBottom: 20, border: "0.5px solid var(--border)" }}>
            {["login", "signup"].map(m => (
              <button key={m} onClick={() => switchMode(m)} style={{
                flex: 1, padding: "8px", border: "none", borderRadius: 7, cursor: "pointer",
                fontSize: 14, fontWeight: 600, fontFamily: "inherit", transition: "all .15s",
                background: mode === m ? "var(--bg)" : "transparent",
                color: mode === m ? "var(--green-dark)" : "var(--text-muted)",
                boxShadow: mode === m ? "0 1px 3px rgba(0,0,0,.08)" : "none"
              }}>
                {m === "login" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>
        )}

        <div className="card">
          {error   && <div className="error-msg">{error}</div>}
          {success && <div style={{ background: "var(--green-light)", border: "1px solid var(--green-mid)", borderRadius: "var(--radius)", padding: "10px 14px", fontSize: 13, color: "var(--green-dark)", marginBottom: 16 }}>{success}</div>}

          {/* ── Login ── */}
          {mode === "login" && (
            <form onSubmit={handleLogin}>
              <div className="form-row">
                <label>Email address</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" required autoComplete="email" />
              </div>
              <div className="form-row">
                <label>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="password" required autoComplete="current-password" />
              </div>
              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? "Signing in..." : "Sign in"}
              </button>
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button type="button" onClick={() => switchMode("forgot")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--text-muted)", textDecoration: "underline" }}>
                  Forgot your password?
                </button>
              </div>
            </form>
          )}

          {/* ── Sign up ── */}
          {mode === "signup" && (
            <form onSubmit={handleSignup}>
              <div className="form-row">
                <label>Full name</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="John Doe" required />
              </div>
              <div className="form-row">
                <label>Email address</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" required autoComplete="email" />
              </div>
              <div className="form-row">
                <label>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Min. 6 characters" required autoComplete="new-password" />
              </div>
              <div className="form-row">
                <label>Phone number (Lebanon)</label>
                <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+961 XX XXX XXX" />
              </div>
              <div className="form-row">
                <label>Delivery address (Lebanon)</label>
                <textarea rows={2} value={address} onChange={e => setAddress(e.target.value)} placeholder="Street, building, floor, city..." />
              </div>
              <div className="form-row">
                <label>Confirm password</label>
                <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                  placeholder="Repeat password" required autoComplete="new-password" />
              </div>
              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? "Creating account..." : "Create account"}
              </button>
              <p style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "center", marginTop: 12 }}>
                A verification email will be sent to confirm your address.
              </p>
            </form>
          )}

          {/* ── Forgot Password ── */}
          {mode === "forgot" && (
            <form onSubmit={handleForgotPassword}>
              <p style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 16, lineHeight: 1.6 }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>
              <div className="form-row">
                <label>Email address</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" required autoComplete="email" />
              </div>
              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? "Sending..." : "Send reset link"}
              </button>
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button type="button" onClick={() => switchMode("login")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--text-muted)", textDecoration: "underline" }}>
                  Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
